import { sysNotify } from '../app-states'
import { playEffect } from '../engine/audio'
import { adminStore } from '../ui/stores/admin-store'
import { buildPreforgeRecipeDefaults, validatePreforgedOffer } from '../commerce/commerce-contract'
import { expandPreforgeDraft } from './preforge-service'
import { formatAIErrorMessage } from '../ai/errors'
import { slugify, titleCase } from '../ui/helpers/admin-preforge-utils'

export const adminPreforgeService = {
  async processBrief(brief: string, onQuickOffer: (offer: any) => void, onUpdatedOffer: (offer: any, message: string) => void): Promise<void> {
    if (!brief) {
      const message = 'Enter a plain-English brief before processing.'
      sysNotify(message)
      throw new Error(message)
    }

    const baseId = slugify(brief) || `reality-${Date.now()}`
    const recipe = buildPreforgeRecipeDefaults({ kind: 'blankPreset', presetId: 'custom' }, 'medium', 'full-reality')
    
    const quickExpansion = {
      draft: {
        brief,
        intentSummary: brief,
        analysis: {
          intentSummary: brief,
          scopeSummary: brief,
          keyEntities: [],
          assumptions: [],
          ambiguities: ['Waiting for the structured extraction pass.'],
          generationNotes: ['Use the draft as an interim editable review structure.'],
        },
        seed: {
          mode: 'create' as const,
          code: `${baseId}-seed`,
          title: titleCase(brief).slice(0, 60) || `${baseId} seed`,
          summary: brief,
        },
        vessel: {
          mode: 'create' as const,
          code: `${baseId}-vessel`,
          name: 'Traveler',
          identity: 'Draft vessel',
          lineage: '',
          sex: 'unspecified' as const,
          bio: brief,
        },
        textlets: {
          worldLore: brief,
          openingPitch: brief,
          npcThreads: brief,
          questThreads: brief,
          itemHooks: brief,
          roomSeeds: brief,
        },
      },
      recipe,
      sourceWorld: recipe.sourceWorld,
      notes: ['Using a fast draft so the composer stays responsive.'],
    }

    const quickOffer = this.buildOfferFromExpansion(baseId, brief, quickExpansion)
    onQuickOffer(quickOffer)

    try {
      const expansion = await expandPreforgeDraft({
        brief,
        sourceWorld: recipe.sourceWorld,
        recipe,
      })

      if (expansion) {
        const updatedOffer = this.buildOfferFromExpansion(baseId, brief, expansion)
        const message = expansion.notes.length
          ? `${expansion.notes[0]} Review the fields and realize when ready.`
          : 'Draft processed. Review the fields and realize when ready.'
        onUpdatedOffer(updatedOffer, message)
        sysNotify('Preforge draft processed.')
      }
    } catch (error) {
      const message = `Could not process brief: ${formatAIErrorMessage(error).slice(0, 160)}`
      sysNotify(message)
      throw error
    }
  },

  buildOfferFromExpansion(baseId: string, brief: string, expansion: any): any {
    const isFull = !!expansion.draft.analysis
    return {
      id: `${baseId}-reality`,
      kind: 'reality',
      title: expansion.draft.seed.title || titleCase(expansion.draft.intentSummary || brief).slice(0, 60),
      summary: expansion.draft.analysis?.scopeSummary || expansion.draft.textlets.openingPitch || brief,
      worldManifestId: `${baseId}-world`,
      seedCode: expansion.draft.seed.code || `${baseId}-seed`,
      vesselCode: expansion.draft.vessel.code || `${baseId}-vessel`,
      starterCode: `${baseId}-starter`,
      includedPlanId: 'pro',
      priceModel: 'included',
      priceCents: 0,
      currency: 'USD',
      publishState: 'draft',
      brief: expansion.draft.brief || brief,
      intentSummary: expansion.draft.intentSummary || brief,
      seedMode: expansion.draft.seed.mode,
      seedTitle: expansion.draft.seed.title,
      seedSummary: expansion.draft.seed.summary,
      vesselMode: expansion.draft.vessel.mode,
      vesselName: expansion.draft.vessel.name,
      vesselIdentity: expansion.draft.vessel.identity,
      vesselLineage: expansion.draft.vessel.lineage,
      vesselSex: expansion.draft.vessel.sex,
      vesselBio: expansion.draft.vessel.bio,
      ...(isFull ? {
        analysisIntentSummary: expansion.draft.analysis?.intentSummary || expansion.draft.intentSummary || brief,
        analysisScopeSummary: expansion.draft.analysis?.scopeSummary || '',
        analysisKeyEntities: (expansion.draft.analysis?.keyEntities || []).join('\n'),
        analysisAssumptions: (expansion.draft.analysis?.assumptions || []).join('\n'),
        analysisAmbiguities: (expansion.draft.analysis?.ambiguities || []).join('\n'),
        analysisGenerationNotes: (expansion.draft.analysis?.generationNotes || []).join('\n'),
      } : {}),
      textletWorldLore: expansion.draft.textlets.worldLore,
      textletOpeningPitch: expansion.draft.textlets.openingPitch,
      textletNpcThreads: expansion.draft.textlets.npcThreads,
      textletQuestThreads: expansion.draft.textlets.questThreads,
      textletItemHooks: expansion.draft.textlets.itemHooks,
      textletRoomSeeds: expansion.draft.textlets.roomSeeds,
      sourceWorldKind: expansion.sourceWorld.kind,
      sourceWorldId: expansion.sourceWorld.kind === 'liveRun' || expansion.sourceWorld.kind === 'savedWorld'
        ? expansion.sourceWorld.saveId
        : expansion.sourceWorld.kind === 'templateWorld'
          ? expansion.sourceWorld.templateId
          : expansion.sourceWorld.presetId,
      scale: expansion.recipe.scale,
      outputMode: expansion.recipe.outputMode,
      densityAtlasNodes: expansion.recipe.density.atlasNodes,
      densityRooms: expansion.recipe.density.rooms,
      densityNpcs: expansion.recipe.density.npcs,
      densityCompanions: expansion.recipe.density.companions,
      densityItems: expansion.recipe.density.items,
      densityQuests: expansion.recipe.density.quests,
      densityClocks: expansion.recipe.density.clocks,
      densityEvents: expansion.recipe.density.events,
      toneMood: expansion.recipe.tone.mood,
      toneDangerLevel: expansion.recipe.tone.dangerLevel,
      toneSocialFocus: expansion.recipe.tone.socialFocus,
      toneExplorationFocus: expansion.recipe.tone.explorationFocus,
      toneCombatFocus: expansion.recipe.tone.combatFocus,
      constraintTokenBudget: expansion.recipe.constraints.tokenBudget,
      constraintTimeBudgetMs: expansion.recipe.constraints.timeBudgetMs,
      constraintForbiddenTopics: expansion.recipe.constraints.forbiddenTopics.join('\n'),
      constraintKeepOutRegions: expansion.recipe.constraints.keepOutRegions.join('\n'),
      forgeReportTitle: '',
      forgeReportDocument: '',
      forgeReportCommercial: '',
      forgeReportMeasures: '',
      forgeReportCompliance: '',
      forgeReportNotes: '',
    }
  },

  async realizeOffer(offer: any): Promise<void> {
    const errors = validatePreforgedOffer(offer)
    if (errors.length) {
      const message = `Preforged offer invalid: ${errors.join('; ')}`
      sysNotify(message)
      throw new Error(message)
    }
    try {
      await adminStore.savePreforgedOffer(offer)
      sysNotify(`Realized ${offer.kind} ${offer.id}.`)
    } catch (error) {
      const message = `Could not realize preforged offer: ${String(error).slice(0, 160)}`
      sysNotify(message)
      throw error
    }
  }
}
